<!-- eslint-disable vue/no-restricted-html-elements -->
<template>
  <splide
    ref="splide"
    class="product-gallery-carousel"
    :class="`product-gallery-carousel--${namespace}`"
    :has-track="false"
    :options="localConfig"
    :aria-label="$string('product.gallery.label', { replace: { product: product.title } })"
    @splide:mounted="handleSplideMounted"
    @splide:moved="handleSplideMoved"
  >
    <splide-track>
      <splide-slide
        v-for="(mediaItem, index) of product.media"
        :key="`media-${product.handle}-${index}`"
      >
        <div
          v-if="getRenderZoom(mediaItem)"
          ref="pinchZoom"
          class="product-gallery-carousel__zoom"
        >
          <div class="product-gallery-carousel__zoom-image-container">
            <img
              class="product-gallery-carousel__zoom-image"
              :alt="mediaItem.alt"
              :loading="index > 0 ? 'lazy' : null"
              :src="mediaItem.image?.url || mediaItem.url"
            >
          </div>
        </div>

        <div
          v-else-if="type !== 'thumbnails' && mediaItem.mediaType === 'video'"
          class="product-gallery-carousel__media-container"
          :class="{
            'product-gallery-carousel__media-container--modal': type === 'modal',
            [$class('loading')]: loading,
          }"
        >
          <video-player
            class="product-gallery-carousel__video"
            :image="mediaItem.previewImage"
            :is-active="getVideoActive(mediaItem.index)"
            :ratio="type === 'modal' ? false : ratio"
            :sizes="sizes"
            :sources="mediaItem.sources"
          />
        </div>

        <div
          v-else
          class="product-gallery-carousel__image-container"
          :class="{ [$class('loading')]: loading }"
        >
          <responsive-image
            class="product-gallery-carousel__image"
            :image="mediaItem.previewImage || mediaItem.image || mediaItem"
            :lazyload="type === 'thumbnails' ? index > 5 : index > 0"
            :min-max="minMax"
            :ratio="ratio"
            :sizes="sizes"
            tabindex="-1"
            @click.prevent="handleImageClick(mediaItem.index)"
          />

          <icon-play
            v-if="type === 'thumbnails' && mediaItem.mediaType === 'video'"
            class="product-gallery-carousel__play-icon"
          />
        </div>
      </splide-slide>
    </splide-track>

    <div class="product-gallery-carousel__arrows splide__arrows">
      <button
        class="splide__arrow splide__arrow--prev"
        type="button"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.carousel.previous')"
        />

        <icon-arrow-left />
      </button>

      <button
        class="splide__arrow splide__arrow--next"
        type="button"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.carousel.next')"
        />

        <icon-arrow-right />
      </button>
    </div>

    <ul class="product-gallery-carousel__pagination splide__pagination" />
  </splide>
</template>

<script>

/**
 * Vue: Product gallery carousel (product-gallery-carousel)
 * -----------------------------------------------------------------------------
 * Splide powered carousel shared between gallery and modal.
 *
 * @param {Object} config - Splide carousel config.
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namesoace.
 * @param {String} [layout] - Current layout.
 * @param {Boolean} [loading] - Loading state.
 * @param {String} minMax - Min max attribute.
 * @param {String} namespace - Namespace, either `gallery` or `modal`.
 * @param {Function} pinchZoomApi - Pinch zoom API, imported into product
 * gallery modal to save file size of main product.
 * @param {Object} product - Local product object.
 * @param {String} ratio - Ratio attribute.
 * @param {String} sizes - Sizes attribute.
 * @param {String} type - Type of carousel, `main`, `thumbnails`, or `modal`.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 *
 * @emits splideMounted - Emit Splide mounted event.
 * @emits updateCurrentIndex -  Emit update current carousel index event.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapActions, mapGetters, mapState } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'
import IconPlay from '~icons/general/play.svg'

export default {
  name: 'ProductGalleryCarousel',

  components: {
    IconArrowLeft,
    IconArrowRight,
    IconPlay,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.video-player' */
        '~async/video-player/video-player'
      ),
    }),
  },

  props: {
    config: {
      type: Object,
      default: () => ({}),
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
    },
    layout: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    minMax: {
      type: String,
      default: '300-1000',
    },
    namespace: {
      type: String,
      default: 'gallery',
    },
    pinchZoomApi: {
      type: Function,
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: () => ({
        handle: '',
        images: [],
        media: [],
        title: '',
      }),
    },
    ratio: {
      type: String,
      default: '1:1',
    },
    sizes: {
      type: String,
      default: '100vw',
    },
    type: {
      type: String,
      default: 'main',
    },
    zoom: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'splide-mounted': () => true,
    'update-current-index': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      localSlideIndex: 0,
      pinchZoom: [],
      pinchZoomActive: false,
      startedPinchZoom: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getRtlWritingDirection: 'getRtlWritingDirection',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if media array has video.
     * @returns {Boolean}
     */
    hasVideo() {
      return this.product?.media?.some((mediaItem) => {
        return this.type !== 'thumbnails' && mediaItem.mediaType === 'video'
      })
    },

    /**
     * Compute local config.
     * @returns {Object}
     */
    localConfig() {
      const localConfig = {
        direction: this.getRtlWritingDirection ? 'rtl' : 'ltr',
        drag: true,
        flickPower: 200,
        flickMaxPages: 1,
        mediaQuery: 'min',
        speed: this.$timing('normal'),
        start: this.currentIndex || 0,
        type: this.product?.media?.length === 1 ? 'slide' : 'loop',
        ...this.config,
      }

      /**
       * Hide arrows and pagination in modal gallery when only one item.
       */
      if (this.product?.media?.length === 1) {
        localConfig.arrows = false
        localConfig.pagination = false
      }

      return localConfig
    },

    /**
     * Compute if current gallery namespace is active.
     * @returns {Boolean}
     */
    namespaceActive() {
      return this.currentNamespace === this.namespace
    },
  },

  watch: {

    /**
     * Watch current media index to update slide index whilst active.
     * - This is used to show variant media.
     */
    currentIndex() {
      if (
        !this.namespaceActive ||
        this.currentIndex === this.localSlideIndex
      ) {
        return
      }

      this.goToIndex(this.currentIndex, true)
    },

    /**
     * Watch has video to load video player when required.
     */
    hasVideo() {
      this.loadVideoPlayer()
    },

    /**
     * Watch is mobile to force refresh the carousel.
     * - Fixes issue where switching from grid to other layout broke the arrows
     *   and pagination.
     */
    async isMobile() {
      if (
        this.namespace !== 'gallery' ||
        !this.isMobile ||
        this.layout === 'grid'
      ) {
        return
      }

      await this.$nextTick()
      this.goToIndex(0)
    },

    /**
     * Watch namespace active to update visible slide and pinch zooms.
     */
    async namespaceActive() {
      if (this.namespaceActive) {
        await this.goToIndex(this.currentIndex)
      }

      this.updatePinchZoom()
    },

    /**
     * Watch pinch zoom active to disable carousel dragging.
     */
    pinchZoomActive() {
      this.localConfig.drag = !this.pinchZoomActive
    },

    /**
     * Watch product to refresh gallery position.
     */
    async product() {
      if (this.namespaceActive) {
        await this.goToIndex(this.currentIndex)
      }

      this.updatePinchZoom()
    },
  },

  mounted() {
    this.loadVideoPlayer()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      openOverlay: 'overlays/open',
    }),

    /**
     * Load video player component.
     */
    loadVideoPlayer() {
      if (!this.hasVideo) {
        return
      }

      this.loadComponent('video-player')
    },

    /**
     * Handle Splide mounted event.
     * @param {Object} splide - Splide instance.
     */
    handleSplideMounted() {
      this.$emit('splide-mounted')

      if (this.type !== 'modal' && !this.pinchZoom.length) {
        return
      }

      this.initPinchZoom()
    },

    /**
     * Handle Splide moved event.
     * - Updates current slide index to share with modal.
     * - Sets pinch zoom container if zoom prop is active.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     * @param {Number} prevIndex - Previous slide index.
     * @param {Number} destIndex - Destination slide index.
     */
    handleSplideMoved(_, newIndex, prevIndex) {
      this.localSlideIndex = newIndex

      if (newIndex === this.currentIndex) {
        return
      }

      this.$emit('update-current-index', newIndex)

      if (this.type !== 'modal') {
        return
      }

      if (this.pinchZoom[prevIndex]) {
        if (this.pinchZoomActive) {
          this.pinchZoom[prevIndex].zoomOut()
        }

        this.pinchZoom[prevIndex].disable()
      }

      if (!this.pinchZoom[newIndex]) {
        return
      }

      this.pinchZoom[newIndex].enable()
    },

    /**
     * Go to slide.
     * - Set speed to 0 to instantly update slide without animation.
     * @param {Number} index - Slide index to go to.
     * @param {Boolean} animate - Animate slide transition.
     * @returns {Promise}
     */
    goToIndex(index, animate = false) {
      return new Promise((resolve) => {
        if (!this.$refs.splide) {
          resolve()
          return
        }

        if (!animate) {
          this.localConfig.speed = 0
        }

        setTimeout(() => {
          this.$refs.splide.go(index)
        }, 0)

        if (!animate) {
          setTimeout(() => {
            this.localConfig.speed = this.config.speed || this.$timing('normal')
          }, 10)
        }

        resolve()
      })
    },

    /**
     * Get if zoom container should be rendered.
     * @param {Object} mediaItem - Current media item.
     * @returns {Boolean}
     */
    getRenderZoom(mediaItem) {
      return (
        this.zoom &&
        this.type === 'modal' &&
        (mediaItem.mediaType === 'image' || !mediaItem.mediaType)
      )
    },

    /**
     * Get video active state.
     * - Play video when gallery is active and slide is visible.
     * @param {Number} index - Video media index.
     * @returns {Boolean}
     */
    getVideoActive(index) {
      return this.layout === 'grid'
        ? this.namespaceActive
        : this.namespaceActive && index === this.currentIndex
    },

    /**
     * Initiate instances of pinch zoom.
     * - Floating index is used in case of mixed media gallery.
     */
    async initPinchZoom() {
      if (typeof this.pinchZoomApi !== 'function') {
        return
      }

      this.startedPinchZoom = true
      await this.$nextTick()

      let floatingIndex = -1

      this.pinchZoom = this.product?.media?.map((mediaItem) => {
        if (mediaItem.mediaType !== 'image') {
          return false
        }

        floatingIndex += 1

        // eslint-disable-next-line new-cap
        return new this.pinchZoomApi(this.$refs.pinchZoom[floatingIndex], {
          draggableUnzoomed: false,
          minZoom: 1,
          onDoubleTap: this.handleZoom,
          onZoomUpdate: this.handleZoom,
          tapZoomFactor: this.isMobile ? 2 : 3,
        })
      })

      /**
       * Disable pinch zooms not currently visible.
       * - Library doesn't provide way of creating instances without
       *   automatically enabling them all so have to disable after init.
       */
      this.pinchZoom.forEach((pinchZoom, index) => {
        if (!pinchZoom || index === this.currentIndex) {
          return
        }

        pinchZoom.disable()
      })
    },

    /**
     * Update instances of pinch zoom when opening/closing modal.
     */
    updatePinchZoom() {
      if (!this.zoom || this.type !== 'modal') {
        return
      }

      /**
       * Init pinch zoom on modal open if it hasn't already started.
       */
      if (this.namespaceActive) {
        if (!this.startedPinchZoom && !this.pinchZoom.length) {
          this.initPinchZoom()
        }

        return
      }

      /**
       * Destroy pinch zoom on modal close.
       * - Updated destroy to not remove elements.
       */
      this.pinchZoom.forEach((pinchZoom) => {
        if (!pinchZoom) {
          return
        }

        pinchZoom.destroy()
      })

      this.pinchZoomActive = false
      this.startedPinchZoom = false
      this.pinchZoom = []
    },

    /**
     * Handle zoom events.
     * - Use events to prevent carousel dragging when zoomed in.
     */
    handleZoom(pinch) {
      this.pinchZoomActive = pinch.zoomFactor > 1
    },

    /**
     * Handle image click.
     * @param {Number} index - Clicked media index.
     */
    handleImageClick(index) {
      if (!this.zoom || this.type === 'modal') {
        return
      }

      this.$emit('update-current-index', index)

      if (this.type === 'thumbnails') {
        return
      }

      this.openOverlay({
        component: 'product-gallery-modal',
        ignoreDismissed: true,
        namespace: 'productGalleryModal',
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery-carousel';
</style>
